import { useState, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Pencil, Save, X, Image as ImageIcon, Upload, Sparkles, Loader2 } from "lucide-react";
import { Button } from "./ui/button";

// InlineEditProvider: gates inline editing to owner and provides API helpers.
export function useOwnerEditMode() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [enabled, setEnabled] = useState(false);
  return { isOwner, editMode: isOwner && enabled, setEnabled };
}

// Floating toggle button (only rendered for owner).
export function OwnerEditToggle({ editMode, setEnabled, onSaved }) {
  return (
    <div className="fixed bottom-6 left-6 z-[70]" data-testid="owner-edit-toggle">
      {!editMode ? (
        <Button onClick={() => setEnabled(true)}
          className="bg-[#1E1611] hover:bg-[#8B5A2A] text-white rounded-full px-4 py-3 shadow-2xl text-xs uppercase tracking-widest font-semibold"
          data-testid="owner-edit-on">
          <Pencil className="w-3.5 h-3.5 mr-1.5"/> Mode Edit
        </Button>
      ) : (
        <div className="bg-[#1E1611] text-white rounded-full pl-4 pr-2 py-1.5 shadow-2xl flex items-center gap-2 text-xs">
          <Sparkles className="w-3.5 h-3.5 text-[#C4A57B]"/>
          <span className="uppercase tracking-widest font-semibold">Mode Edit Aktif</span>
          <button onClick={() => { setEnabled(false); onSaved && onSaved(); }}
            className="ml-2 bg-[#C4A57B] hover:bg-white text-[#1E1611] rounded-full px-3 py-1.5 font-semibold uppercase tracking-widest"
            data-testid="owner-edit-off">
            <X className="w-3.5 h-3.5 inline mr-1"/> Selesai
          </button>
        </div>
      )}
    </div>
  );
}

// Wraps a text element; when editMode is true, click-to-edit via prompt/inline.
export function EditableText({ editMode, value, onSave, multiline = false, className = "", placeholder = "(kosong — klik untuk isi)", testid }) {
  const [busy, setBusy] = useState(false);
  const handle = async () => {
    if (!editMode) return;
    const next = multiline
      ? window.prompt("Edit teks (bisa multi-baris, gunakan \\n untuk enter):", value || "")
      : window.prompt("Edit teks:", value || "");
    if (next === null) return;
    const cleaned = multiline ? next.replace(/\\n/g, "\n") : next;
    if (cleaned === value) return;
    setBusy(true);
    try { await onSave(cleaned); toast.success("Tersimpan"); }
    catch (e) { toast.error("Gagal simpan"); }
    finally { setBusy(false); }
  };
  const isEmpty = !value || !value.trim();
  return (
    <span
      onClick={handle}
      className={`${className} ${editMode ? "outline-2 outline-dashed outline-[#C4A57B]/60 outline-offset-2 rounded cursor-pointer hover:bg-[#C4A57B]/10 transition-colors relative" : ""} ${isEmpty && editMode ? "text-[#C4A57B]/80 italic" : ""}`}
      data-testid={testid}
    >
      {isEmpty && editMode ? placeholder : (value || "")}
      {editMode && (
        <span className="absolute -top-2 -right-2 bg-[#8B5A2A] text-white rounded-full w-5 h-5 flex items-center justify-center shadow-md" title="Klik untuk edit">
          {busy ? <Loader2 className="w-2.5 h-2.5 animate-spin"/> : <Pencil className="w-2.5 h-2.5"/>}
        </span>
      )}
    </span>
  );
}

// Wraps an image; when editMode, adds click-to-upload overlay.
export function EditableImage({ editMode, src, alt, imageClassName, onUploaded, label = "Ganti Foto", testid }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("image_category", "Hero");
      fd.append("label", alt || label);
      const { data } = await api.post("/cms/landing/gallery/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await onUploaded(data.id);
      toast.success("Foto tersimpan");
    } catch (e) { toast.error("Gagal upload"); }
    finally { setBusy(false); }
  };
  return (
    <div className={`relative ${editMode ? "outline-2 outline-dashed outline-[#C4A57B] outline-offset-2 rounded-xl group" : ""}`} data-testid={testid}>
      <img src={src} alt={alt} className={imageClassName}/>
      {editMode && (
        <>
          <input ref={ref} type="file" accept="image/*" className="hidden" onChange={(e)=>upload(e.target.files?.[0])}/>
          <button
            onClick={()=>ref.current?.click()}
            disabled={busy}
            className="absolute inset-0 flex items-center justify-center bg-[#1E1611]/60 opacity-0 group-hover:opacity-100 transition-opacity text-white font-semibold text-xs uppercase tracking-widest rounded-xl"
            data-testid={`${testid}-btn`}
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5"/> : <Upload className="w-4 h-4 mr-1.5"/>}
            {label}
          </button>
        </>
      )}
    </div>
  );
}
